import React, { useState } from 'react';
import { Stack, Typography, FormControlLabel, Switch, Box, Button } from '@mui/material';

import { BasePage } from '../../components/base-page';
import { useQuery } from '@apollo/client/react';
import { GetInvoicesForAccountDocument, GetInvoicesForAccountQuery } from '../../generated/graphql';
import { useNavigate, useSearchParams } from 'react-router';
import { appPaths } from '../../app-paths';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../components/app-context';

import { InvoiceDataGrid } from '../../components/invoice-datagrid';

const FilterGroupsArray = ['all', 'drafts', 'unpaid', 'overdue'] as const;
type FilterGroupType = (typeof FilterGroupsArray)[number];

export function InvoiceListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { show } = useAlert();
  const app = useApp();
  const { data, loading, error, refetch } = useQuery<GetInvoicesForAccountQuery>(GetInvoicesForAccountDocument);

  React.useEffect(() => {
    if (error) {
      show.error('Nepodarilo sa načítať faktúry.', error.message);
    }
  }, [error, show]);

  const filterGroup: FilterGroupType = (searchParams.get('fg') as FilterGroupType) ?? 'all';
  if (filterGroup && FilterGroupsArray.includes(filterGroup as FilterGroupType) === false) {
    searchParams.set('fg', 'all');
    setSearchParams(searchParams);
  }

  const handleFilterGroupChange = (g: FilterGroupType) => {
    searchParams.set('fg', g);
    setSearchParams(searchParams);
  };

  const invoices = data?.getInvoicesForAccount ?? [];

  // Filter invoices based on toggle states
  const filteredInvoices = React.useMemo(
    () =>
      invoices.filter((invoice) => {
        // Always exclude deleted invoices
        if (invoice.deletedAt) return false;

        const isDraft = invoice.isDraft;

        // Drafts tab: show only drafts
        if (filterGroup === 'drafts') return isDraft;

        // All other tabs: exclude drafts
        if (isDraft) return false;

        const isUnPaid = !invoice.paidAt;
        const isOverdue = isUnPaid && invoice.dueDate && new Date(invoice.dueDate).getTime() < Date.now();

        if (!isOverdue && filterGroup === 'overdue') return false;
        if (!isUnPaid && filterGroup === 'unpaid') return false;

        return true;
      }),
    [invoices, filterGroup],
  );

  return (
    <BasePage title="Faktúry" loading={loading} errorMsg={error ? 'Nepodarilo sa načítať faktúry.' : undefined}>
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }} useFlexGap flexWrap="wrap">
          <Button
            variant={filterGroup != 'all' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('all')}
          >
            Všetky
          </Button>
          <Button
            variant={filterGroup != 'drafts' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('drafts')}
          >
            Návrhy
          </Button>
          <Button
            variant={filterGroup != 'unpaid' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('unpaid')}
          >
            Nezaplatené
          </Button>
          <Button
            variant={filterGroup != 'overdue' ? 'outlined' : 'contained'}
            onClick={() => handleFilterGroupChange('overdue')}
          >
            Po splatnosti
          </Button>
        </Stack>

        <Box sx={{ height: 600, width: '100%' }}>
          <InvoiceDataGrid
            invoices={filteredInvoices}
            formatNumber={app.formatNumber}
            formatDate={app.formatDate}
            onRowClick={(invoiceId) => navigate(appPaths.invoice.navigate({ invoiceId }))}
          />
        </Box>
      </Box>
    </BasePage>
  );
}
